// Context-switcher dedup (fix/offline-context-dedup, 2026-10-30).
//
// Mirror of the SERVER collapse in api/src/database/account-contexts.js
// (getActiveContextsForUser, 2026-10-29 dedup): ONE condo entry per
// (condo_id, unit_id), keeping the highest-precedence role. Personal rows pass
// through untouched.
//
// Why the frontend needs this: GET /auth/me returns one membership row per
// user_condo_roles row (UserRoles.getUserRoles does not collapse), and the
// switcher renders that raw list. ONLINE the duplicates are usually masked
// because the /units/mine owned-unit entries replace the membership rows for
// the same units (AppShell/MobileMenu hide membership rows covered by an owned
// unit). OFFLINE that fetch never runs, so ownedUnits is empty and every raw
// role row renders — admin+resident on the same unit (or propietario+resident
// on a standalone home) showed as two identical entries. Collapsing here makes
// the switcher identical online and offline regardless of ownedUnits state.
//
// Same precedence order as the API collapse (lower rank wins):
// propietario > admin/super_admin/sub_admin > treasurer > resident >
// familiar > empleado-class > other.

export interface CollapsibleMembershipRow {
  id?: string | null;
  role?: string | null;
  condoId?: string | null;
  condo_id?: string | null;
  unitId?: string | null;
  unit_id?: string | null;
}

const ROLE_PRECEDENCE: Record<string, number> = {
  propietario: 0,
  admin: 1,
  super_admin: 1,
  sub_admin: 1,
  treasurer: 2,
  resident: 3,
  familiar: 4,
  security: 5,
  maintenance_staff: 5,
  empleado: 5,
};

const rankOf = (row: CollapsibleMembershipRow): number => {
  const role = String(row.role ?? '');
  return Object.prototype.hasOwnProperty.call(ROLE_PRECEDENCE, role) ? ROLE_PRECEDENCE[role] : 6;
};

const keyOf = (row: CollapsibleMembershipRow): string =>
  `${String(row.condoId ?? row.condo_id ?? '')}|${String(row.unitId ?? row.unit_id ?? '')}`;

/**
 * Collapse membership rows to one per (condo_id, unit_id), keeping the
 * highest-precedence role. Ties keep the first row (server-side the rows
 * arrive in a deterministic order; same rule as the API's strict `<` compare).
 * Row order of the surviving rows is preserved.
 */
export function collapseMembershipRows<T extends CollapsibleMembershipRow>(rows: T[] | undefined | null): T[] {
  const list = Array.isArray(rows) ? rows : [];
  const keep = new Map<string, T>();
  for (const row of list) {
    const key = keyOf(row);
    const prev = keep.get(key);
    if (!prev || rankOf(row) < rankOf(prev)) keep.set(key, row);
  }
  return list.filter((row) => keep.get(keyOf(row)) === row);
}
